import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { api } from "../../api/apiService";
import { cardSx, errorText, fieldSx, menuProps } from "./fields";

type ModelConnection = {
  id: string;
  name: string;
  kind: string;
  base_url: string;
  model: string;
  key_configured: boolean;
  token_hint: string;
  enabled: boolean;
};

const kinds = [
  { value: "openrouter", label: "OpenRouter" },
  { value: "anthropic", label: "Anthropic" },
  { value: "openai", label: "OpenAI" },
  { value: "compatible", label: "OpenAI-compatible" },
];

const defaultModels: Record<string, string> = {
  openrouter: "anthropic/claude-sonnet-4.5",
  anthropic: "claude-sonnet-4-5",
  openai: "gpt-4.1",
  compatible: "",
};

function kindLabel(kind: string): string {
  return kinds.find((item) => item.value === kind)?.label || kind;
}

function asModels(data: unknown): ModelConnection[] {
  const rows = Array.isArray(data)
    ? data
    : data && typeof data === "object" && Array.isArray((data as { providers?: unknown }).providers)
      ? (data as { providers: unknown[] }).providers
      : [];
  return rows.filter((row): row is ModelConnection => {
    return Boolean(row) && typeof row === "object" && typeof (row as ModelConnection).id === "string";
  });
}

export function ModelsList() {
  const [models, setModels] = useState<ModelConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [kind, setKind] = useState("openrouter");
  const [model, setModel] = useState(defaultModels.openrouter);
  const [baseURL, setBaseURL] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [enabled, setEnabled] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editKind, setEditKind] = useState("openrouter");
  const [editModel, setEditModel] = useState("");
  const [editBaseURL, setEditBaseURL] = useState("");
  const [editKey, setEditKey] = useState("");
  const [editEnabled, setEditEnabled] = useState(false);

  const load = useCallback(async () => {
    const data = await api.get("/api/agent-studio/providers");
    setModels(asModels(data));
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await load();
      } catch (err) {
        if (!cancelled) setFormError(errorText(err, "Could not load models."));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  const payload = (row: {
    name: string;
    kind: string;
    model: string;
    base_url: string;
    api_key: string;
    enabled: boolean;
  }) => ({
    name: row.name.trim(),
    kind: row.kind,
    model: row.model.trim(),
    base_url: row.kind === "compatible" ? row.base_url.trim() : "",
    api_key: row.api_key.trim(),
    enabled: row.enabled,
  });

  const resetAdd = () => {
    setName("");
    setKind("openrouter");
    setModel(defaultModels.openrouter);
    setBaseURL("");
    setApiKey("");
    setEnabled(false);
  };

  const handleAdd = async () => {
    if (enabled && !apiKey.trim()) {
      setFormError("Set an API key before enabling this model.");
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      await api.post("/api/agent-studio/providers", payload({ name, kind, model, base_url: baseURL, api_key: apiKey, enabled }));
      resetAdd();
      await load();
    } catch (err) {
      setFormError(errorText(err, "Could not register this model."));
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (row: ModelConnection) => {
    if (editEnabled && !editKey.trim() && !row.key_configured) {
      setFormError("Set an API key before enabling this model.");
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      await api.put(
        `/api/agent-studio/providers/${row.id}`,
        payload({
          name: editName,
          kind: editKind,
          model: editModel,
          base_url: editBaseURL,
          api_key: editKey,
          enabled: editEnabled,
        })
      );
      setEditingId(null);
      setEditKey("");
      await load();
    } catch (err) {
      setFormError(errorText(err, "Could not update this model."));
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (row: ModelConnection, next: boolean) => {
    if (next && !row.key_configured) {
      setFormError("Set an API key before enabling this model.");
      return;
    }
    setFormError(null);
    setSaving(true);
    try {
      await api.put(
        `/api/agent-studio/providers/${row.id}`,
        payload({
          name: row.name,
          kind: row.kind,
          model: row.model,
          base_url: row.base_url,
          api_key: "",
          enabled: next,
        })
      );
      await load();
    } catch (err) {
      setFormError(errorText(err, "Could not update this model."));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this model?")) return;
    setSaving(true);
    setFormError(null);
    try {
      await api.delete(`/api/agent-studio/providers/${id}`);
      if (editingId === id) setEditingId(null);
      await load();
    } catch (err) {
      setFormError(errorText(err, "Could not delete this model."));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row: ModelConnection) => {
    setEditingId(row.id);
    setEditName(row.name);
    setEditKind(row.kind);
    setEditModel(row.model);
    setEditBaseURL(row.base_url || "");
    setEditKey("");
    setEditEnabled(row.enabled);
    setFormError(null);
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6" sx={{ color: "#fafafa", fontWeight: 600 }}>
          Models
        </Typography>
        <Typography variant="body2" sx={{ color: "#a1a1aa", mt: 0.5 }}>
          Register a model the way you register a publish venue. The API key is stored once and is not shown again. Enabling one model turns the others off.
        </Typography>
      </Box>

      {formError && <Alert severity="error">{formError}</Alert>}

      <Stack spacing={2} sx={cardSx}>
        <Typography variant="subtitle2" sx={{ color: "#e4e4e7" }}>
          Add a model
        </Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "flex-start" }} flexWrap="wrap">
          <Box sx={{ flex: 1, minWidth: { xs: "100%", md: 140 } }}>
            <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} size="small" disabled={saving} sx={fieldSx} />
          </Box>
          <Box sx={{ flex: 1, minWidth: { xs: "100%", md: 160 } }}>
            <TextField
              select
              label="Provider"
              value={kind}
              onChange={(e) => {
                const next = e.target.value;
                setKind(next);
                setModel(defaultModels[next] || "");
              }}
              size="small"
              disabled={saving}
              sx={fieldSx}
              SelectProps={{ MenuProps: menuProps }}
            >
              {kinds.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ flex: 1.4, minWidth: { xs: "100%", md: 180 } }}>
            <TextField label="Model" value={model} onChange={(e) => setModel(e.target.value)} size="small" disabled={saving} sx={fieldSx} />
          </Box>
          {kind === "compatible" && (
            <Box sx={{ flex: 1.4, minWidth: { xs: "100%", md: 200 } }}>
              <TextField
                label="Base URL"
                value={baseURL}
                onChange={(e) => setBaseURL(e.target.value)}
                size="small"
                disabled={saving}
                placeholder="https://…"
                sx={fieldSx}
              />
            </Box>
          )}
          <Box sx={{ flex: 1.2, minWidth: { xs: "100%", md: 160 } }}>
            <TextField
              label="API key"
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              size="small"
              disabled={saving}
              sx={fieldSx}
            />
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                disabled={saving}
                onChange={(_, next) => {
                  if (next && !apiKey.trim()) {
                    setFormError("Set an API key before enabling this model.");
                    return;
                  }
                  setFormError(null);
                  setEnabled(next);
                }}
              />
            }
            label="Enabled"
            sx={{ color: "#d4d4d8", m: 0, mt: { md: "6px" }, flexShrink: 0 }}
          />
          <Tooltip title="Add model">
            <span>
              <IconButton
                aria-label="Add model"
                disabled={saving || !name.trim() || !model.trim()}
                onClick={() => void handleAdd()}
                sx={{ color: "#818cf8" }}
              >
                <CheckIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {loading && (
        <Typography variant="body2" sx={{ color: "#71717a" }}>
          Loading models…
        </Typography>
      )}
      {!loading && models.length === 0 && (
        <Typography variant="body2" sx={{ color: "#71717a" }}>
          No models yet.
        </Typography>
      )}

      {models.map((row) => (
        <Stack key={row.id} spacing={1.5} sx={cardSx}>
          {editingId === row.id ? (
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "flex-start" }} flexWrap="wrap">
              <Box sx={{ flex: 1, minWidth: { xs: "100%", md: 140 } }}>
                <TextField label="Name" value={editName} onChange={(e) => setEditName(e.target.value)} size="small" disabled={saving} sx={fieldSx} />
              </Box>
              <Box sx={{ flex: 1, minWidth: { xs: "100%", md: 160 } }}>
                <TextField
                  select
                  label="Provider"
                  value={editKind}
                  onChange={(e) => setEditKind(e.target.value)}
                  size="small"
                  disabled={saving}
                  sx={fieldSx}
                  SelectProps={{ MenuProps: menuProps }}
                >
                  {kinds.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box sx={{ flex: 1.4, minWidth: { xs: "100%", md: 180 } }}>
                <TextField label="Model" value={editModel} onChange={(e) => setEditModel(e.target.value)} size="small" disabled={saving} sx={fieldSx} />
              </Box>
              {editKind === "compatible" && (
                <Box sx={{ flex: 1.4, minWidth: { xs: "100%", md: 200 } }}>
                  <TextField label="Base URL" value={editBaseURL} onChange={(e) => setEditBaseURL(e.target.value)} size="small" disabled={saving} sx={fieldSx} />
                </Box>
              )}
              <Box sx={{ flex: 1.2, minWidth: { xs: "100%", md: 180 } }}>
                <TextField
                  label="API key"
                  type="password"
                  value={editKey}
                  onChange={(e) => setEditKey(e.target.value)}
                  size="small"
                  disabled={saving}
                  placeholder={row.key_configured ? "Leave blank to keep the current key" : ""}
                  sx={fieldSx}
                />
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={editEnabled}
                    disabled={saving}
                    onChange={(_, next) => {
                      if (next && !editKey.trim() && !row.key_configured) {
                        setFormError("Set an API key before enabling this model.");
                        return;
                      }
                      setFormError(null);
                      setEditEnabled(next);
                    }}
                  />
                }
                label="Enabled"
                sx={{ color: "#d4d4d8", m: 0, mt: { md: "6px" } }}
              />
              <Stack direction="row">
                <Tooltip title="Save">
                  <span>
                    <IconButton aria-label="Save model" disabled={saving || !editName.trim() || !editModel.trim()} onClick={() => void saveEdit(row)} sx={{ color: "#818cf8" }}>
                      <CheckIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Cancel">
                  <IconButton aria-label="Cancel edit" onClick={() => setEditingId(null)} sx={{ color: "#a1a1aa" }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          ) : (
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ color: "#fafafa", fontWeight: 600 }}>{row.name}</Typography>
                <Typography variant="body2" sx={{ color: "#a1a1aa" }}>
                  {kindLabel(row.kind)} · {row.model}
                  {row.kind === "compatible" && row.base_url ? ` · ${row.base_url}` : ""}
                </Typography>
                <Typography variant="caption" sx={{ color: "#71717a" }}>
                  {row.key_configured ? `Key stored${row.token_hint ? ` (${row.token_hint})` : ""}` : "No API key"}
                </Typography>
              </Box>
              <FormControlLabel
                control={<Switch checked={row.enabled} disabled={saving} onChange={(_, next) => void toggleEnabled(row, next)} />}
                label="Enabled"
                sx={{ color: "#d4d4d8", m: 0, flexShrink: 0 }}
              />
              <Tooltip title="Edit">
                <IconButton aria-label={`Edit ${row.name}`} onClick={() => startEdit(row)} sx={{ color: "#a1a1aa" }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton aria-label={`Delete ${row.name}`} disabled={saving} onClick={() => void handleDelete(row.id)} sx={{ color: "#a1a1aa" }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Stack>
      ))}
    </Stack>
  );
}
